import { City, Program, ProgramsOutsideVehicle, RouteForVehicles } from '@/new-transport/_models/transport-map.model';
import { Status } from '@/common/enums/Status';
import { OfferTransport, StopKind, TransportTerm } from '@/new-transport/_models/transport.model';
import { calculateParticipantsAmountFromPrograms } from '@/new-transport/_utils/calculate-participants-amount-from-programs/calculate-participants-amount-from-programs';

export const assignProgramsToCities = (
	offerTransport: OfferTransport[],
	cities: City[],
	stopKind: 'stopDeparture' | 'stopReturn',
	programsOutsideVehicle: ProgramsOutsideVehicle[],
	routeForVehicles: RouteForVehicles,
	transportStops: TransportTerm['transportStop'][],
): City[] => {
	return cities.map((city) => {
		const peopleInCity = offerTransport.filter((transport) => {
			return transport.slot.some((v) => v[stopKind]?.stop.id === city.id);
		});

		const programsFromOfferTransport: Program[] = peopleInCity.reduce((acc, transport) => {
			let program: Program = acc.find((v) => v.destinationId === transport.product.id);
			if (!program) {
				program = {
					destinationId: transport.product.stop,
					destinationName:
						transportStops.find((v) => v.id === transport.product.stop)?.address ?? transport.product.name,
					reservation: new Map(),
					confirmedParticipants: 0,
					optionParticipants: 0,
					fromCity: city.id,
				};
				acc.push(program);
			}

			transport.slot.forEach((slot) => {
				if (!slot[stopKind]) {
					return;
				}
				if (slot[stopKind].stop.id === city.id) {
					program.reservation.set(
						transport.reservation.id,
						program.reservation.has(transport.reservation.id)
							? [...program.reservation.get(transport.reservation.id), slot]
							: [slot],
					);

					if (slot.status === Status.CONFIRMED) {
						program.confirmedParticipants++;
					}
					if (slot.status === Status.OPTION) {
						program.optionParticipants++;
					}
				}
			});

			return acc;
		}, []);

		const foundProgramsOutsideVehicle = programsOutsideVehicle.find((v) => v.cityId === city.id);
		const alreadyTakenSlotsByVehicle = Array.from(routeForVehicles.entries())
			.map(([key, route]) => {
				const takenSlotsIds = route
					.filter((v) => v.cityId === city.id && v.fromCity === city.id)
					.flatMap((v) => (v.action === StopKind.BOARDING ? v.slots.map((slot) => slot.id) : null))
					.filter(Boolean);
				const alreadyTakenSlots = calculateParticipantsAmountFromPrograms(
					filterReservationByAlreadyTakenSlots(programsFromOfferTransport, takenSlotsIds),
				);
				return alreadyTakenSlots
					? {
							vehicleId: key,
							alreadyTakenSlots,
						}
					: null;
			})
			.filter(Boolean);

		return {
			...city,
			programsFromOfferTransport: programsFromOfferTransport,
			programsOutsideVehicle: foundProgramsOutsideVehicle ? foundProgramsOutsideVehicle.programs : [],
			antennaParticipants: foundProgramsOutsideVehicle
				? calculateParticipantsAmountFromPrograms(
						filterReservationsSlotsByCityId(foundProgramsOutsideVehicle, stopKind),
					)
				: 0,
			alreadyTakenSlotsByVehicle,
		};
	});
};

export function filterReservationByAlreadyTakenSlots(
	programsFromOfferTransport: Program[],
	takenStopsIds: number[],
): Program[] {
	return programsFromOfferTransport.map((program) => {
		const filteredReservations = new Map(
			Array.from(program.reservation.entries()).map(([key, slots]) => {
				const filteredSlots = slots.filter((slot) => {
					return takenStopsIds.includes(slot.id);
				});
				return [key, filteredSlots];
			}),
		);

		return {
			...program,
			reservation: filteredReservations,
		};
	});
}

export function filterReservationsSlotsByCityId(
	foundProgramsOutsideVehicle: ProgramsOutsideVehicle,
	stopKind: string,
): Program[] {
	return foundProgramsOutsideVehicle?.programs.map((program) => {
		const filteredReservations = new Map(
			Array.from(program.reservation.entries()).map(([key, slots]) => {
				const filteredSlots = slots.filter((slot) => {
					return slot[stopKind].stop.id !== foundProgramsOutsideVehicle.cityId;
				});
				return [key, filteredSlots];
			}),
		);

		return {
			...program,
			reservation: filteredReservations,
		};
	});
}
